import { NetworkStatus, useApolloClient, useMutation } from '@apollo/client';
import { ErrorBoundary, ErrorRetry, MediaUpload, SectionHeader, SuspenseLoader } from '@bechtel/client-common';
import { CLAIM_WELD, RECLAIM_WELD } from '@bechtel/client-gql';
import {
  Box,
  Separator,
  SeparatorType,
  SideSheetBody,
  SideSheetContentCol,
  SideSheetFooter,
  Spinner,
  Text,
  ToastAction,
  ToastType,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  ClaimWeldInput,
  ClaimWeldPayload,
  CrewMemberDiscipline,
  CrewMemberList,
  CrewMemberSortByTerm,
  PersonaType,
  ReclaimWeldInput,
  ReclaimWeldPayload,
  RejectWeldReason,
  RejectWeldSubReason,
} from '@bechtel/types';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Formik, FormikProps } from 'formik';
import { get, join, map } from 'lodash';
import { FC, Fragment, Suspense, useEffect, useState } from 'react';
import { array, number, object } from 'yup';
import { SendBackReasonMessages, SendBackSubReasonMessages, WeldProcedure } from '~/components/atoms';
import { Comment, LocationDescription, StencilSelector, WeldedLocation } from '~/components/molecules';
import { useShowMap } from '~/hooks';
import { useClaimAndApproveWeld } from '~/hooks/use-claim-and-approve-weld';
import { useCrewMember } from '~/hooks/use-crew-member';
import { ClaimWeldErrorModal } from './error/internals';
import { ClaimWeldErrorExt } from './error/types';
import { FooterButtonStyled } from './styled';
import { ClaimReclaimWeldProps, FormikState } from './types';

const claimWeldValidationSchema = object().shape({
  weldProcedures: array()
    .required()
    .min(1, t`Select at least 1 weld procedure`),
  stencils: array()
    .required()
    .min(1, t`Select at least 1 stencil`),
  mediaMetadata: array()
    .required()
    .max(3, t`Max 3 photos can be uploaded`),
  weldedLocation: object().when('showMap', {
    is: true,
    then: object()
      .shape({
        latitude: number(),
        longitude: number(),
      })
      .required(t`Location is mandatory`),
    otherwise: object().nullable(),
  }),
});

export const ClaimReclaimWeld: FC<ClaimReclaimWeldProps> = ({
  isClaimAndApprove,
  weldCommodity,
  close,
  isReclaim,
  refetchQueries,
}) => {
  const { weldedWithProcedure, location, locationDescription } = weldCommodity;
  const { i18n } = useLingui();
  const { showMap } = useShowMap();
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const weldCurrentMilestone = weldCommodity?.milestones.find((milestone) => {
    return milestone?.type?.id === weldCommodity?.currentMilestoneType?.id;
  });
  const commodityMilestoneId = weldCurrentMilestone?.id ? weldCurrentMilestone?.id : '';
  const dispatchToast = useToastsDispatch();
  const [claimWeldError, SetClaimWeldError] = useState<ClaimWeldErrorExt | null>();
  const client = useApolloClient();
  const [
    getWelderList,
    {
      data: crewMemberData,
      error: crewMemberError,
      loading: crewMemberLoading,
      refetch: crewMemberRefetch,
      networkStatus: crewMemberNetworkStatus,
    },
  ] = useCrewMember();
  const [stencilSearchQuery, setStencilSearchQuery] = useState('');
  const crewMemberList = get(crewMemberData, 'crewMembers.items', []) as CrewMemberList['items'];
  const selectedStencilList = weldCommodity?.commodityAssignees
    ?.filter((commodityAssignee) => commodityAssignee.personaType === PersonaType.CRAFT)
    .map((commodityAssignee) => {
      return commodityAssignee.assignee;
    });

  let stencilsData = selectedStencilList;

  const initialValues: FormikState = {
    showMap: showMap,
    locationDesc: locationDescription,
    mediaMetadata: [],
    stencils: stencilsData && stencilsData.length > 0 ? stencilsData : [],
    weldProcedures: weldedWithProcedure ? weldedWithProcedure : [],
    weldedLocation:
      showMap && !!location?.latitude && !!location?.longitude
        ? {
            latitude: location?.latitude,
            longitude: location?.longitude,
          }
        : null,
    comments: '',
  };
  const [claimWeld, { loading: mutationLoading }] = useMutation<
    { claimWeldPayload: ClaimWeldPayload },
    { input: ClaimWeldInput }
  >(CLAIM_WELD, {
    onCompleted: async (data) => {
      const error = get(data, 'claimWeld.error');
      if (error) {
        SetClaimWeldError(error);
      } else {
        SetClaimWeldError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: t`Claimed Weld successfully`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        close();
        await client.refetchQueries({
          include: refetchQueries,
        });
      }
    },
    onError: (error) => {
      SetClaimWeldError(error);
    },
  });

  const [reclaimWeld, { loading: reclaimMutationLoading }] = useMutation<
    { reclaimWeldPayload: ReclaimWeldPayload },
    { input: ReclaimWeldInput }
  >(RECLAIM_WELD, {
    onCompleted: async (data) => {
      const error = get(data, 'reclaimWeld.error');
      if (error) {
        SetClaimWeldError(error);
      } else {
        SetClaimWeldError(null);
        const toastAction: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: t`Reclaimed Weld successfully`,
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(toastAction);
        close();
        await client.refetchQueries({
          include: refetchQueries,
        });
      }
    },
    onError: (error) => {
      SetClaimWeldError(error);
    },
  });

  useEffect(() => {
    if (!actionCompleted) return;
    const error = get(claimApproveData, 'claimApproveWeld.error') || claimApproveError;
    if (!error) {
      const claimApproveWeldToast: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.SUCCESS,
          content: `${t`Weld claimed and approved successfully.`}`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(claimApproveWeldToast);
      close && close();
    } else {
      SetClaimWeldError(error);
    }
    setActionCompleted(false);
  }, [actionCompleted]);
  const [
    claimAndApproveWeld,
    { loading: claimApproveMutationLoading, error: claimApproveError, data: claimApproveData },
  ] = useClaimAndApproveWeld(() => {
    setActionCompleted(true);
  }, [
    'commodities',
    'commodityDashboardTabStats',
    'assignToMeStatsFWE',
    'commodityWeeklyStats',
    'commodityStatsAllPriorityStats',
  ]);

  const closeModal = () => {
    SetClaimWeldError(null);
  };

  useEffect(() => {
    if (stencilSearchQuery.length > 0 && stencilSearchQuery.length < 2) return;
    const input = {
      discipline: CrewMemberDiscipline.WELDING,
      sortBy: CrewMemberSortByTerm.WELDER_STENCIL,
    };
    getWelderList({
      variables: {
        input: {
          ...input,
          searchKeyword: stencilSearchQuery,
        },
        paging: {
          cursor: '1',
          limit: 50,
        },
      },
    });
  }, [getWelderList, stencilSearchQuery]);

  const rejectionSubReasonTypes =
    isReclaim &&
    weldCurrentMilestone?.currentStatus?.rejectionSubReasonTypes &&
    weldCurrentMilestone?.currentStatus?.rejectionSubReasonTypes.length > 0
      ? weldCurrentMilestone?.currentStatus?.rejectionSubReasonTypes
      : [];

  const getWeldProcedureIds = (arr) => arr.map((weld) => weld.id) ?? null;

  return (
    <>
      <Formik
        validationSchema={claimWeldValidationSchema}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={async (values) => {
          if (isReclaim) {
            const input: ReclaimWeldInput = {
              commodityMilestoneId: commodityMilestoneId,
              stencilIds: map(values.stencils, (item) => item.id),
              weldProcedureIds: values.weldProcedures ? getWeldProcedureIds(values.weldProcedures) : [],
              location: values?.weldedLocation
                ? {
                    latitude: parseFloat(values?.weldedLocation?.latitude),
                    longitude: parseFloat(values?.weldedLocation?.longitude),
                  }
                : undefined,
              locationDescription: values.locationDesc,
              attachmentMetadata: values.mediaMetadata,
              comments: values.comments,
            };

            await reclaimWeld({
              variables: { input },
            });
          } else {
            const input: ClaimWeldInput = {
              commodityMilestoneId: commodityMilestoneId,
              stencilIds: map(values.stencils, (item) => item.id),
              weldProcedureIds: getWeldProcedureIds(values.weldProcedures),
              location: values?.weldedLocation
                ? {
                    latitude: parseFloat(values?.weldedLocation?.latitude),
                    longitude: parseFloat(values?.weldedLocation?.longitude),
                  }
                : undefined,
              locationDescription: values.locationDesc,
              attachmentMetadata: values.mediaMetadata,
            };
            if (isClaimAndApprove) {
              await claimAndApproveWeld({
                variables: { input },
              });
            } else {
              await claimWeld({
                variables: { input },
              });
            }
          }
        }}
      >
        {(props: FormikProps<FormikState>) => {
          const { handleSubmit, dirty, isValid, values } = props;
          return (
            <Fragment>
              <SideSheetBody>
                <SideSheetContentCol>
                  {isReclaim && weldCurrentMilestone?.currentStatus?.rejectionType?.reason && (
                    <>
                      <SectionHeader title={t`Sent Back Reason`} />
                      <Box
                        flexDirection={{ sm: 'column' }}
                        px={{ sm: '450' }}
                        pt={{ sm: '500' }}
                        pb={
                          rejectionSubReasonTypes && rejectionSubReasonTypes.length > 0 ? { sm: '450' } : { sm: '500' }
                        }
                      >
                        <Text size="400" color="grey.1000" weight="600">
                          <Trans>Reason</Trans>
                        </Text>
                        <Text size="400" style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
                          {weldCurrentMilestone?.currentStatus?.rejectionType?.reason == RejectWeldReason.OTHER_REASON
                            ? weldCurrentMilestone?.currentStatus?.comments
                            : i18n._(
                                SendBackReasonMessages[weldCurrentMilestone?.currentStatus?.rejectionType?.reason]
                              )}
                        </Text>
                      </Box>
                      {rejectionSubReasonTypes.length > 0 && (
                        <>
                          <Separator type={SeparatorType.HORIZONTAL} />
                          <Box
                            flexDirection={{ sm: 'column' }}
                            px={{ sm: '450' }}
                            pb={{ sm: '500' }}
                            pt={{ sm: '400' }}
                          >
                            <Text size="400" color="grey.1000" weight="600">
                              <Trans> Sub Reasons(s)</Trans>
                            </Text>
                            <Text size="400" style={{ wordBreak: 'break-word', lineHeight: 'normal' }}>
                              {join(
                                map(rejectionSubReasonTypes, (item) => {
                                  return item.reason == RejectWeldSubReason.OTHER_SUB_REASON
                                    ? item.comments
                                    : i18n._(SendBackSubReasonMessages[item.reason]);
                                }),
                                ', '
                              )}
                            </Text>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                  <SectionHeader title={t`Weld Procedure`} />
                  <WeldProcedure fieldName="weldProcedures" items={weldCommodity?.applicableWeldProcedures} />
                  <SectionHeader title={t`Stencils`} />
                  <Box flexDirection={{ sm: 'column' }} px={{ sm: '450' }} py={{ sm: '500' }}>
                    {!crewMemberLoading && crewMemberError && (
                      <div data-testid="welder-error">
                        <ErrorRetry refetch={crewMemberRefetch}>
                          <Trans>There was a problem loading Stencils</Trans>
                        </ErrorRetry>
                      </div>
                    )}
                    {!crewMemberError && (
                      <StencilSelector
                        fieldName="stencils"
                        items={crewMemberList}
                        enableClientSearch={false}
                        onSearch={(query) => {
                          setStencilSearchQuery(query);
                        }}
                        loading={crewMemberLoading || crewMemberNetworkStatus === NetworkStatus.refetch}
                      />
                    )}
                  </Box>
                  <SectionHeader title={t`Location`} />
                  <Box flexDirection={{ sm: 'column' }} px={{ sm: '450' }} py={{ sm: '500' }}>
                    {values.showMap && <WeldedLocation name="weldedLocation" />}
                    <LocationDescription name="locationDesc" />
                  </Box>
                  {isReclaim && (
                    <>
                      <SectionHeader title={t`Resolution Details`} />
                      <Comment name="comments" />
                    </>
                  )}
                  <SectionHeader
                    title={`${t`Photo(s)`} (${values?.mediaMetadata?.length ? values.mediaMetadata.length : 0}/3)`}
                  />
                  <MediaUpload fieldName="mediaMetadata" />
                </SideSheetContentCol>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  <Box mr={{ sm: '400' }}>
                    <FooterButtonStyled
                      data-testid="weld-cancel"
                      disabled={mutationLoading || reclaimMutationLoading || claimApproveMutationLoading}
                      onClick={close}
                    >
                      <Trans> Cancel</Trans>
                    </FooterButtonStyled>
                  </Box>
                  <Box>
                    <FooterButtonStyled
                      data-testid="weld-submit"
                      disabled={
                        claimApproveMutationLoading ||
                        reclaimMutationLoading ||
                        mutationLoading ||
                        !dirty ||
                        !isValid ||
                        (isReclaim && !values.comments)
                      }
                      onClick={() => handleSubmit()}
                    >
                      {(mutationLoading || reclaimMutationLoading || claimApproveMutationLoading) && <Spinner />}
                      {!mutationLoading && !reclaimMutationLoading && !claimApproveMutationLoading && (
                        <Text> {isClaimAndApprove ? t`Claim & Approve` : isReclaim ? t`Reclaim` : t`Claim`}</Text>
                      )}
                    </FooterButtonStyled>
                  </Box>
                </Box>
              </SideSheetFooter>
            </Fragment>
          );
        }}
      </Formik>
      {claimWeldError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <ClaimWeldErrorModal error={claimWeldError} close={closeModal} />
          </Suspense>
        </ErrorBoundary>
      )}
    </>
  );
};
